<template>
  <b-modal v-model="model" size="2xl">
    <template #header>
      <div class="px-10 pt-10 pb-2">
        <h1 class="mb-2">{{ $t('experience_split.title') }}</h1>
        <p class="text-gray-400 text-sm">
          {{ $t('experience_split.subtitle', { company: source.company || '', title: source.title }) }}
        </p>
      </div>
    </template>

    <template #default>
      <div class="px-10 py-5 space-y-4">
        <div
          v-for="(period, index) in periods"
          :key="index"
          class="bg-gray-900/50 rounded-xl p-4 border border-gray-700 space-y-3"
        >
          <div class="flex items-center justify-between">
            <span class="text-white text-sm font-medium">{{ $t('experience_split.period_n', { n: index + 1 }) }}</span>
            <button v-if="periods.length > 2" type="button" class="text-red-400 text-xs hover:underline" @click="removePeriod(index)">
              {{ $t('experience_split.remove_period') }}
            </button>
          </div>

          <b-text-field v-model="period.title" :label="$t('experience_split.job_title')" />

          <div class="flex gap-3">
            <b-text-field v-model="period.start" type="month" :label="$t('experience_split.start')" class="flex-1" />
            <b-text-field
              v-if="period.end !== null"
              v-model="period.end"
              type="month"
              :label="$t('experience_split.end')"
              class="flex-1"
            />
            <div v-else class="flex-1 flex items-end pb-2 text-gray-400 text-sm">{{ $t('experience_split.ongoing') }}</div>
          </div>

          <b-checkbox
            v-if="index === periods.length - 1 && sourceIsOngoing"
            :model-value="period.end === null"
            :label="$t('experience_split.current_position')"
            @update:model-value="(checked) => toggleOngoing(index, checked)"
          />

          <b-chip-input
            searchable
            :items="skillsItems"
            :model-value="skillChipsList[index] || []"
            :label="$t('experience_split.skills')"
            :max-items="5"
            @update:model-value="(v) => onSkillsChange(index, v)"
          />
        </div>

        <button type="button" class="text-primary text-sm hover:underline" @click="addPeriod">
          + {{ $t('experience_split.add_period') }}
        </button>

        <ul v-if="errors.length" class="text-red-400 text-xs list-disc pl-4">
          <li v-for="err in errors" :key="err">{{ $t(err) }}</li>
        </ul>
      </div>
    </template>

    <template #footer>
      <div class="px-10 pb-10 pt-2 flex gap-4">
        <b-button light block @click="model = false">{{ $t('common.cancel') }}</b-button>
        <b-button block :disabled="errors.length > 0" @click="handleConfirm">
          {{ $t('experience_split.confirm') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import BModal from '~/components/modal/BModal.vue';
import BButton from '~/components/forms/BButton.vue';
import BTextField from '~/components/forms/BTextField.vue';
import BCheckbox from '~/components/forms/checkbox/BCheckbox.vue';
import BChipInput from '~/components/forms/chip-input/BChipInput.vue';
import { groupByCategory } from '~/utils/categoryGrouping';
import { buildSeedPeriods, validateSplitPeriods, type SplitPeriod } from '~/help/experienceSplit';

interface SkillChip { value: string; label: string }

interface Props {
  source: { title: string; company?: string; start: string; end: string | null };
}
const props = defineProps<Props>();
const emit = defineEmits<{ (e: 'confirm', periods: SplitPeriod[]): void }>();
const model = defineModel<boolean>({ default: false });

// useSkillStore is auto-imported by Nuxt (Pinia store auto-import), same as
// BModalCreateExperienceStep3.vue which uses it without an explicit import.
const skillStore = useSkillStore();
const { skills } = storeToRefs(skillStore);
const skillsItems = computed(() => groupByCategory(skills.value || []));

const periods = ref<SplitPeriod[]>([]);
// Chip {value,label} objects per period, kept in lockstep with `periods`
// (same index, spliced together) so removals never desync skills from rows.
const skillChipsList = ref<SkillChip[][]>([]);

const sourceIsOngoing = computed(() => props.source.end === null);

const errors = computed(() => validateSplitPeriods(periods.value, props.source.start, props.source.end));

function onSkillsChange(index: number, chips: SkillChip[]) {
  skillChipsList.value[index] = chips;
  periods.value[index].skill_ids = chips.map((c) => c.value);
}

function toggleOngoing(index: number, checked: boolean) {
  periods.value[index].end = checked ? null : (props.source.end ?? '');
}

function addPeriod() {
  periods.value.push({ title: props.source.title, start: '', end: props.source.end === null ? null : '', skill_ids: [] });
  skillChipsList.value.push([]);
}

function removePeriod(index: number) {
  periods.value.splice(index, 1);
  skillChipsList.value.splice(index, 1);
}

function handleConfirm() {
  if (errors.value.length) return;
  emit('confirm', JSON.parse(JSON.stringify(periods.value)));
  model.value = false;
}

// Seed the editor each time it opens.
watch(model, (open) => {
  if (open) {
    skillStore.fetchSkills();
    periods.value = buildSeedPeriods(props.source.title, props.source.start, props.source.end);
    skillChipsList.value = periods.value.map(() => []);
  }
});
</script>
